// On-Site Verification Service
// Manages attendee verification for live events
// Verified attendees can request/vote/rank; unverified are read-only spectators

import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { getEventById } from './events';

const VERIFIED_PREFIX = 'votebeats_verified_';

// Check if an attendee is verified for a specific event (Firestore)
export async function isAttendeeVerified(eventId: string, oderId: string): Promise<boolean> {
  const q = query(
    collection(db, 'verifiedAttendees'),
    where('eventId', '==', eventId),
    where('oderId', '==', oderId)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

// Verify an attendee by validating token and creating verification doc
export async function verifyAttendee(
  eventId: string,
  oderId: string,
  token: string
): Promise<{ success: boolean; message: string }> {
  // Fetch event and validate token
  const event = await getEventById(eventId);
  if (!event) {
    return { success: false, message: 'Event not found.' };
  }
  if (!event.settings.onSiteVerificationEnabled) {
    return { success: false, message: 'On-site verification is not enabled for this event.' };
  }
  if (event.settings.onSiteVerificationToken !== token) {
    return { success: false, message: 'Invalid verification code. Please scan the QR code at the venue.' };
  }

  // Check if already verified
  const alreadyVerified = await isAttendeeVerified(eventId, oderId);
  if (alreadyVerified) {
    return { success: true, message: 'You\'re already verified!' };
  }

  // Create verification document
  await addDoc(collection(db, 'verifiedAttendees'), {
    eventId,
    oderId,
    token,
    verifiedAt: serverTimestamp(),
  });

  // Cache in localStorage
  setVerifiedCache(eventId, true);

  return { success: true, message: 'You\'re verified! You can now request songs and vote.' };
}

// Get count of verified attendees for an event
export async function getVerifiedAttendeeCount(eventId: string): Promise<number> {
  const q = query(
    collection(db, 'verifiedAttendees'),
    where('eventId', '==', eventId)
  );
  const snapshot = await getDocs(q);
  return snapshot.size;
}

// Revoke all verifications for an event (when token is regenerated)
export async function revokeAllVerifications(eventId: string): Promise<void> {
  const q = query(
    collection(db, 'verifiedAttendees'),
    where('eventId', '==', eventId)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return;

  const batch = writeBatch(db);
  snapshot.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
}

// Delete verified attendees for an event (cascade delete)
export async function deleteVerifiedAttendeesByEventId(eventId: string): Promise<void> {
  await revokeAllVerifications(eventId);
}

// localStorage cache helpers to reduce Firestore reads
export function getVerifiedCache(eventId: string): boolean {
  return localStorage.getItem(`${VERIFIED_PREFIX}${eventId}`) === 'true';
}

export function setVerifiedCache(eventId: string, verified: boolean): void {
  if (verified) {
    localStorage.setItem(`${VERIFIED_PREFIX}${eventId}`, 'true');
  } else {
    localStorage.removeItem(`${VERIFIED_PREFIX}${eventId}`);
  }
}

export function clearVerifiedCache(eventId: string): void {
  localStorage.removeItem(`${VERIFIED_PREFIX}${eventId}`);
}
